import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

interface HumanTaskTemplate {
  key: string;
  label: string;
  category: 'finance' | 'executive';
  summary: string;
  audience: string;
  tags: string[];
  defaultSchedule: {
    cron: string;
    timezone: string;
    cadenceLabel: string;
  };
  defaultJob: {
    name: string;
    description: string;
  };
  defaultTask: {
    title: string;
    description: string;
    entityType: string;
    priority: 'P0' | 'P1' | 'P2';
    checklist: string[];
    dedupeScope: 'none' | 'day' | 'month';
    createApproval: boolean;
    reviewerRole?: string;
    requesterRole?: string;
    approvalTitle?: string;
    approvalDescription?: string;
    timeoutMinutes?: number;
  };
}

interface HumanTaskJob {
  id: string;
  name: string;
  description: string | null;
  cron: string | null;
  timezone: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  runCount: number;
  isActive: boolean;
  templateKey: string | null;
  templateLabel: string | null;
  title: string | null;
  taskDescription: string | null;
  assignedTo: string | null;
  priority: string | null;
  createApproval: boolean;
  reviewerRole: string | null;
  checklist: string[];
}

interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: string;
}

interface RoleRow {
  id: string;
  name: string;
}

interface TemplateFormState {
  name: string;
  title: string;
  taskDescription: string;
  cron: string;
  timezone: string;
  assignedTo: string;
  priority: 'P0' | 'P1' | 'P2';
  createApproval: boolean;
  reviewerRole: string;
  timeoutMinutes: string;
  checklistText: string;
}

function buildInitialForm(template: HumanTaskTemplate | null): TemplateFormState {
  if (!template) {
    return {
      name: '',
      title: '',
      taskDescription: '',
      cron: '0 9 * * 1',
      timezone: 'Asia/Singapore',
      assignedTo: '',
      priority: 'P1',
      createApproval: false,
      reviewerRole: '',
      timeoutMinutes: '240',
      checklistText: '',
    };
  }
  return {
    name: template.defaultJob.name,
    title: template.defaultTask.title,
    taskDescription: template.defaultTask.description,
    cron: template.defaultSchedule.cron,
    timezone: template.defaultSchedule.timezone,
    assignedTo: '',
    priority: template.defaultTask.priority,
    createApproval: template.defaultTask.createApproval,
    reviewerRole: template.defaultTask.reviewerRole ?? '',
    timeoutMinutes: String(template.defaultTask.timeoutMinutes ?? 240),
    checklistText: template.defaultTask.checklist.join('\n'),
  };
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function RecurringTaskTemplatesPage() {
  const [templates, setTemplates] = useState<HumanTaskTemplate[]>([]);
  const [jobs, setJobs] = useState<HumanTaskJob[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [form, setForm] = useState<TemplateFormState>(buildInitialForm(null));
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updatingJobId, setUpdatingJobId] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.key === selectedKey) ?? templates[0] ?? null,
    [selectedKey, templates],
  );

  const memberNameMap = useMemo(() => {
    const entries = members.map((member) => [
      member.userId,
      member.name?.trim() ? `${member.name} (${member.email})` : member.email,
    ]);
    return Object.fromEntries(entries);
  }, [members]);

  const activeJobs = useMemo(() => jobs.filter((job) => job.isActive), [jobs]);
  const approvalJobs = useMemo(() => activeJobs.filter((job) => job.createApproval), [activeJobs]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [templateRes, jobRes, memberRes, roleRes] = await Promise.allSettled([
        api.get<HumanTaskTemplate[]>('/automation/human-task-templates'),
        api.get<HumanTaskJob[]>('/automation/human-task-jobs'),
        api.get<Member[]>('/admin/members'),
        api.get<RoleRow[]>('/admin/roles'),
      ]);

      if (templateRes.status === 'fulfilled') {
        const nextTemplates = Array.isArray(templateRes.value) ? templateRes.value : [];
        setTemplates(nextTemplates);
        setSelectedKey((current) => current || nextTemplates[0]?.key || '');
      }
      if (jobRes.status === 'fulfilled') {
        setJobs(Array.isArray(jobRes.value) ? jobRes.value : []);
      }
      if (memberRes.status === 'fulfilled') {
        setMembers(Array.isArray(memberRes.value) ? memberRes.value : []);
      } else {
        setMembers([]);
      }
      if (roleRes.status === 'fulfilled') {
        setRoles(roleRes.value.map((row) => row.name));
      } else {
        setRoles([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setForm(buildInitialForm(selectedTemplate));
  }, [selectedTemplate]);

  const handleCreate = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    try {
      const checklist = form.checklistText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      const timeoutMinutes = Number.parseInt(form.timeoutMinutes || '240', 10);
      const trimmedName = form.name.trim();
      const trimmedTitle = form.title.trim();
      const trimmedTaskDescription = form.taskDescription.trim();
      const trimmedCron = form.cron.trim();
      const trimmedTimezone = form.timezone.trim();
      const payload = {
        ...(trimmedName ? { name: trimmedName } : {}),
        ...(trimmedTitle ? { title: trimmedTitle } : {}),
        ...(trimmedTaskDescription ? { taskDescription: trimmedTaskDescription } : {}),
        ...(trimmedCron ? { cron: trimmedCron } : {}),
        ...(trimmedTimezone ? { timezone: trimmedTimezone } : {}),
        assignedTo: form.assignedTo || undefined,
        priority: form.priority,
        createApproval: form.createApproval,
        reviewerRole: form.reviewerRole || undefined,
        timeoutMinutes: Number.isFinite(timeoutMinutes) ? timeoutMinutes : 240,
        checklist,
      };

      await api.post(`/automation/human-task-templates/${encodeURIComponent(selectedTemplate.key)}/create`, payload);
      toast.success(`已创建 recurring pack: ${selectedTemplate.label}`);
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '创建 recurring pack 失败');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleJob = async (job: HumanTaskJob) => {
    setUpdatingJobId(job.id);
    try {
      await api.patch(`/automation/human-task-jobs/${job.id}`, { isActive: !job.isActive });
      toast.success(job.isActive ? '已暂停 recurring pack' : '已恢复 recurring pack');
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新 recurring pack 状态失败');
    } finally {
      setUpdatingJobId(null);
    }
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ClipboardList className="h-6 w-6 text-primary" />
            Recurring Human Task Packs
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            用同一套 tenant scheduler substrate 配 recurring human work，不开第四套调度。
            先把 finance close、AR review、vendor reconciliation 和 executive review 做成成品。
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          刷新
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">内建模板</CardTitle>
            <CardDescription>业务化 recurring packs</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-3xl font-semibold">{templates.length}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">启用中 Packs</CardTitle>
            <CardDescription>当前 tenant 正在运行</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-3xl font-semibold">{activeJobs.length}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">带审批 Packs</CardTitle>
            <CardDescription>需要 manager/admin review</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-3xl font-semibold">{approvalJobs.length}</CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card>
          <CardHeader>
            <CardTitle>模板库</CardTitle>
            <CardDescription>选择一个业务模板，再针对当前 tenant 做轻量配置。</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {templates.map((template) => {
              const activeCount = jobs.filter((job) => job.templateKey === template.key && job.isActive).length;
              const selected = template.key === (selectedTemplate?.key ?? '');
              return (
                <button
                  key={template.key}
                  type="button"
                  onClick={() => setSelectedKey(template.key)}
                  className={cn(
                    'rounded-xl border p-4 text-left transition-colors',
                    selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{template.label}</div>
                      <div className="mt-1 text-sm text-muted-foreground">{template.summary}</div>
                    </div>
                    <Badge variant="secondary" className="capitalize">
                      {template.category}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="h-3.5 w-3.5" />
                      {template.defaultSchedule.cadenceLabel}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {template.audience}
                    </span>
                    {activeCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        已启用 {activeCount}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {template.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[10px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>已启用 Recurring Packs</CardTitle>
            <CardDescription>直接启停，不需要切去 platform-admin 查询 tenant 调度表。</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {jobs.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                当前还没有 recurring human task pack。
              </div>
            )}

            {jobs.map((job) => (
              <div key={job.id} className="rounded-xl border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{job.name}</div>
                      <Badge className={job.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {job.isActive ? 'active' : 'paused'}
                      </Badge>
                      {job.createApproval && (
                        <Badge variant="outline" className="border-amber-300 text-amber-700">
                          approval
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {job.templateLabel ?? job.title ?? job.description ?? 'Recurring human task'}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={updatingJobId === job.id}
                    onClick={() => void handleToggleJob(job)}
                  >
                    {updatingJobId === job.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : job.isActive ? (
                      <PauseCircle className="mr-2 h-4 w-4" />
                    ) : (
                      <PlayCircle className="mr-2 h-4 w-4" />
                    )}
                    {job.isActive ? '暂停' : '恢复'}
                  </Button>
                </div>

                <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-2">
                  <div>Cron: {job.cron ?? '—'} {job.timezone ? `(${job.timezone})` : ''}</div>
                  <div>Next run: {formatDateTime(job.nextRunAt)}</div>
                  <div>Last run: {formatDateTime(job.lastRunAt)}</div>
                  <div>Run count: {job.runCount}</div>
                  <div>Owner: {job.assignedTo ? (memberNameMap[job.assignedTo] ?? job.assignedTo) : 'Unassigned'}</div>
                  <div>Reviewer: {job.reviewerRole ?? '—'}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>配置当前模板</CardTitle>
          <CardDescription>
            {selectedTemplate
              ? `${selectedTemplate.label}：${selectedTemplate.summary}`
              : '请选择模板'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          {selectedTemplate && (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="pack-name">Pack Name</Label>
                  <Input
                    id="pack-name"
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="pack-title">Work Item Title</Label>
                  <Input
                    id="pack-title"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="pack-cron">Cron</Label>
                  <Input
                    id="pack-cron"
                    value={form.cron}
                    onChange={(event) => setForm((current) => ({ ...current, cron: event.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Recommended: {selectedTemplate.defaultSchedule.cadenceLabel}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="pack-timezone">Timezone</Label>
                  <Input
                    id="pack-timezone"
                    value={form.timezone}
                    onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Assigned Owner</Label>
                  {members.length > 0 ? (
                    <Select value={form.assignedTo || '__unassigned__'} onValueChange={(value) => setForm((current) => ({
                      ...current,
                      assignedTo: value === '__unassigned__' ? '' : value,
                    }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">Unassigned</SelectItem>
                        {members.map((member) => (
                          <SelectItem key={member.userId} value={member.userId}>
                            {member.name?.trim() ? `${member.name} (${member.email})` : member.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      placeholder="Tenant user id"
                      value={form.assignedTo}
                      onChange={(event) => setForm((current) => ({ ...current, assignedTo: event.target.value }))}
                    />
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(value) => setForm((current) => ({
                    ...current,
                    priority: value as TemplateFormState['priority'],
                  }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P0">P0</SelectItem>
                      <SelectItem value="P1">P1</SelectItem>
                      <SelectItem value="P2">P2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="grid gap-2">
                  <Label htmlFor="task-description">Task Description</Label>
                  <Textarea
                    id="task-description"
                    rows={5}
                    value={form.taskDescription}
                    onChange={(event) => setForm((current) => ({ ...current, taskDescription: event.target.value }))}
                  />
                </div>
                <div className="rounded-xl border bg-muted/20 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    Approval Settings
                  </div>
                  <div className="mt-4 flex items-center space-x-2">
                    <Checkbox
                      id="create-approval"
                      checked={form.createApproval}
                      onCheckedChange={(checked) => setForm((current) => ({
                        ...current,
                        createApproval: checked === true,
                      }))}
                    />
                    <Label htmlFor="create-approval">Create approval request</Label>
                  </div>
                  <div className="mt-4 grid gap-3">
                    <div className="grid gap-2">
                      <Label>Reviewer Role</Label>
                      {roles.length > 0 ? (
                        <Select value={form.reviewerRole || '__none__'} onValueChange={(value) => setForm((current) => ({
                          ...current,
                          reviewerRole: value === '__none__' ? '' : value,
                        }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reviewer role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {roles.map((role) => (
                              <SelectItem key={role} value={role}>{role}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          placeholder="manager"
                          value={form.reviewerRole}
                          onChange={(event) => setForm((current) => ({ ...current, reviewerRole: event.target.value }))}
                        />
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="timeout-minutes">Approval Timeout Minutes</Label>
                      <Input
                        id="timeout-minutes"
                        type="number"
                        value={form.timeoutMinutes}
                        onChange={(event) => setForm((current) => ({ ...current, timeoutMinutes: event.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="checklist-text">Checklist</Label>
                <Textarea
                  id="checklist-text"
                  rows={6}
                  value={form.checklistText}
                  onChange={(event) => setForm((current) => ({ ...current, checklistText: event.target.value }))}
                  placeholder="One checklist item per line"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/20 p-4">
                <div className="text-sm text-muted-foreground">
                  当前模板会落到 `scheduled_jobs`，执行器是 `human.create_recurring_work_item`，产出到 `entity_work_items`。
                </div>
                <Button onClick={() => void handleCreate()} disabled={creating || !selectedTemplate}>
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
                  创建 Recurring Pack
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
